import type { LearnTopic } from "../topic-types";

/**
 * People-Pleasing After Trauma.
 */
export const PEOPLE_PLEASING_TOPIC: LearnTopic = {
  slug: "people-pleasing",
  emoji: "🫶",
  title: "People-Pleasing After Trauma",
  tagline: "When keeping others happy became safety",
  theme: "peach",
  category: "bonds-boundaries",
  seoDescription:
    "Understand why people-pleasing and the fawn response develop after trauma, and practise small ways to reconnect with your own needs. Trauma-informed education from Recovery Trauma™.",
  cardBlurb: "Why fawning develops, and gentle ways to reconnect with your own needs.",
  isNew: true,
  intro: [
    "People-pleasing is often described as a personality trait — being \"too nice\" or \"a pushover.\" For many trauma survivors, it's something else entirely: a survival strategy that once kept you safe by keeping others calm, happy, and unlikely to hurt you.",
    "This pattern, sometimes called the fawn response, can mean saying yes when you mean no, apologising for things that aren't your fault, and losing track of what you actually want somewhere along the way. It can feel less like a choice and more like an automatic setting.",
    "This guide explores why people-pleasing develops, how to recognise it in daily life, and small, low-pressure ways to start reconnecting with your own preferences — at whatever pace feels right for you.",
  ],
  closing:
    "Reconnecting with your own needs after years of prioritising everyone else's is slow, gentle work. Small steps count, and there's no deadline. Our Boundaries guide pairs well with this one when you're ready.",
  continueTo: [
    { label: "Boundaries", to: "/learn/boundaries" },
    { label: "Trauma Responses", to: "/learn/trauma-responses" },
    { label: "Inner Critic", to: "/learn/inner-critic" },
    { label: "Journal", to: "/journal" },
  ],
  sections: [
    {
      slug: "understand",
      emoji: "📚",
      title: "Understand: where people-pleasing comes from",
      blurb: "The fawn response, and why it develops.",
      resources: [
        {
          slug: "why-people-pleasing-develops",
          kind: "learn",
          title: "Why people-pleasing develops",
          summary: "How keeping others happy can become a survival strategy.",
          minutes: 5,
          blocks: [
            {
              type: "prose",
              text: "If conflict, anger or disapproval once felt dangerous — whether physically, emotionally, or to a relationship you depended on — appeasing others can become one of the safest available strategies. Over time, this can turn into an automatic way of relating to almost everyone, not just the people who were originally unsafe.",
            },
            {
              type: "list",
              title: "People-pleasing can develop after",
              items: [
                "Growing up with a caregiver whose moods were unpredictable",
                "Being punished, shamed or withdrawn from for expressing needs",
                "Learning that being \"easy\" or \"good\" earned safety or affection",
                "Past relationships where conflict escalated quickly or dangerously",
                "Environments where your needs were consistently less important than someone else's",
              ],
            },
            {
              type: "note",
              text: "This pattern made sense as a response to real circumstances. It isn't a character flaw, and it isn't something to be ashamed of — it's something you can gently learn your way out of, at your own pace.",
            },
          ],
        },
        {
          slug: "the-fawn-response",
          kind: "learn",
          title: "The fawn response",
          summary: "A fourth survival response, alongside fight, flight and freeze.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "Alongside fight, flight and freeze, some describe a fourth trauma response: fawn. Instead of confronting, escaping or shutting down, fawning moves toward the source of threat and tries to soothe it — appeasing, agreeing, and prioritising someone else's comfort in order to reduce danger.",
            },
            {
              type: "list",
              title: "The fawn response can look like",
              items: [
                "Instantly agreeing with someone, even when you don't",
                "Scanning a room or a face for signs of tension before you've spoken",
                "Feeling responsible for other people's moods",
                "A reflex to comfort someone who has upset you",
                "Difficulty identifying what you feel, only what others might feel",
              ],
            },
            {
              type: "prose",
              text: "Fawning is not weakness — it is often a highly effective way of surviving situations where confronting or escaping wasn't possible or safe.",
            },
          ],
        },
        {
          slug: "fear-of-rejection-and-conflict",
          kind: "learn",
          title: "Fear of rejection and fear of conflict",
          summary: "The two fears that often sit underneath people-pleasing.",
          minutes: 4,
          blocks: [
            {
              type: "list",
              title: "Fear of rejection can sound like",
              items: [
                "\"If I disagree, they might not like me anymore.\"",
                "\"If I say no, they'll find someone easier to be around.\"",
                "\"My value depends on being useful or agreeable.\"",
              ],
            },
            {
              type: "list",
              title: "Fear of conflict can sound like",
              items: [
                "\"Any disagreement could turn into something much bigger.\"",
                "\"It's safer to just go along with it.\"",
                "\"I'd rather feel resentful than deal with tension.\"",
              ],
            },
            {
              type: "prose",
              text: "Both fears are usually rooted in real past experience, even when the current relationship or situation is genuinely safer. Recognising the fear as old, rather than untrue, can be a useful first step.",
            },
          ],
        },
      ],
    },
    {
      slug: "recognise",
      emoji: "🔎",
      title: "Recognise: how it shows up day to day",
      blurb: "Difficulty saying no, over-apologising, reassurance-seeking, and losing touch with your own needs.",
      resources: [
        {
          slug: "signs-of-people-pleasing",
          kind: "learn",
          title: "Everyday signs of people-pleasing",
          summary: "Common patterns worth noticing without judgement.",
          minutes: 5,
          blocks: [
            {
              type: "list",
              title: "Difficulty saying no",
              items: [
                "Agreeing to plans, favours or requests before you've actually thought about them",
                "Saying yes and then feeling resentful or exhausted afterwards",
                "Finding excuses rather than simply declining",
              ],
            },
            {
              type: "list",
              title: "Over-apologising",
              items: [
                "Saying sorry for things that aren't your fault, or for existing in someone's way",
                "Apologising for having a need, a feeling, or a different opinion",
                "Apologising more than once for the same small thing",
              ],
            },
            {
              type: "list",
              title: "Seeking reassurance",
              items: [
                "Repeatedly checking whether someone is upset with you",
                "Needing frequent confirmation that a relationship is okay",
                "Replaying conversations, searching for signs you did something wrong",
              ],
            },
            {
              type: "list",
              title: "Losing contact with your own needs",
              items: [
                "Struggling to answer \"what do you want to do?\"",
                "Feeling unsure what you actually think until you know what others think",
                "Noticing exhaustion or resentment before you notice the need underneath it",
              ],
            },
          ],
        },
        {
          slug: "cost-of-people-pleasing",
          kind: "learn",
          title: "The cost of people-pleasing",
          summary: "What this pattern can quietly take from your life over time.",
          minutes: 3,
          blocks: [
            {
              type: "prose",
              text: "People-pleasing often looks kind from the outside, and it can genuinely include real generosity. But when it comes from fear rather than choice, it tends to come at a cost — burnout, resentment, a sense of not really being known by the people closest to you, and a quiet loneliness even in the middle of full social lives.",
            },
            {
              type: "note",
              text: "Noticing this cost isn't about blaming yourself for adapting — it's about giving yourself permission to explore doing things differently.",
            },
          ],
        },
      ],
    },
    {
      slug: "practise",
      emoji: "🌿",
      title: "Practise: reconnecting with what you want",
      blurb: "Small, low-pressure ways to practise choice.",
      resources: [
        {
          slug: "what-do-i-actually-want",
          kind: "reflect",
          title: "What do I actually want?",
          summary: "A reflection for reconnecting with your own preferences.",
          minutes: 6,
          blocks: [
            {
              type: "prose",
              text: "If you've spent a long time reading other people's needs before your own, this question can feel surprisingly hard — even about small things. That's common, and it tends to ease with gentle practice.",
            },
            {
              type: "prompts",
              title: "Reflection prompts",
              items: [
                "If no one else's opinion was in the room, what would I choose right now?",
                "When did I last do something purely because I wanted to, not because it was expected?",
                "What's a small preference I overrode today, and why?",
                "If I imagine a friend in my exact situation, what would I want for them?",
                "What feeling shows up first when I try to answer \"what do I want\" — and can I be curious about it rather than pushing it away?",
              ],
            },
          ],
        },
        {
          slug: "small-choices-practice",
          kind: "practice",
          title: "A week of small choices",
          summary: "A gentle practice for noticing and expressing tiny preferences.",
          minutes: 7,
          blocks: [
            {
              type: "prose",
              text: "Rebuilding a connection to your own needs often works best in small, low-stakes moments — not the biggest decision in your life, but the smallest ones, repeated often.",
            },
            {
              type: "steps",
              title: "Try this for a week",
              items: [
                { label: "Pick one low-stakes choice a day", note: "What to eat, what to watch, which route to walk." },
                { label: "Pause before defaulting to someone else's preference", note: "Even a few seconds of pause counts." },
                { label: "Voice it, if it feels safe to", note: "\"Actually, I'd like...\" — practising the words matters as much as the outcome." },
                { label: "If voicing it doesn't feel safe yet, just notice it privately", note: "Naming a preference to yourself is still valuable practice." },
                { label: "Notice how it feels afterwards", note: "Without needing it to feel entirely comfortable straight away." },
              ],
            },
            {
              type: "note",
              text: "This isn't about becoming demanding or difficult — it's about restoring a muscle that trauma made it necessary to set aside.",
            },
          ],
        },
        {
          slug: "responding-to-guilt",
          kind: "learn",
          title: "When guilt shows up",
          summary: "What to do when practising choice brings up discomfort.",
          minutes: 3,
          blocks: [
            {
              type: "prose",
              text: "Guilt, anxiety or a strong urge to backtrack are common when you first start expressing preferences after years of suppressing them. This doesn't mean you've done something wrong — it usually means an old, protective pattern has noticed a change.",
            },
            {
              type: "list",
              title: "It can help to",
              items: [
                "Remind yourself the guilt is a familiar reflex, not evidence of harm",
                "Let the feeling be present without automatically undoing the choice",
                "Start with the very smallest preferences first, and build from there",
                "Talk it through with someone who supports your growth, or write it down",
              ],
            },
          ],
        },
      ],
    },
    {
      slug: "reflect-and-plan",
      emoji: "✍️",
      title: "Reflect and plan",
      blurb: "A worksheet for tracking your own patterns and progress.",
      resources: [
        {
          slug: "people-pleasing-worksheet",
          kind: "worksheet",
          title: "Noticing my patterns worksheet",
          summary: "A printable worksheet for tracking people-pleasing moments and practising choice.",
          minutes: 8,
          worksheetFields: [
            { label: "A recent moment I said yes but meant no", hint: "Describe what happened.", lines: 3 },
            { label: "What I was afraid would happen if I said no", lines: 2 },
            { label: "What I actually wanted in that moment", lines: 2 },
            { label: "One small preference I can practise voicing this week", lines: 2 },
            { label: "Someone I feel safest practising this with", hint: "A person, or simply yourself in private.", lines: 2 },
            { label: "How I'll be kind to myself if it feels uncomfortable", lines: 2 },
          ],
        },
      ],
    },
  ],
};
